import React, {useState, useMemo, useRef} from 'react';
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input
} from 'reactstrap';
import {Plus, Search} from 'lucide-react';
import {useResourceAllocation} from '../../resource_allocation/context/ResourceAllocationContext.tsx';
import DisplayTag from './common/DisplayTag';
import {Tag, TagWithTempId, TagScope} from '../../types';
import {
    getRandomTagColor,
    getRandomTagIcon,
    finalizeTagName,
    tagAppliesTo,
    getDescendantIds,
    flattenTreeForDisplay
} from '../../lib/tagUtils';
import {v4 as uuidv4} from 'uuid';

interface TagSelectorProps {
    selectedTags: (Tag | TagWithTempId)[];
    onChange: (tags: (Tag | TagWithTempId)[]) => void;
    placeholder?: string;
    isLoading?: boolean;
    className?: string;
    allowCreate?: boolean;
    // Only show tags applicable to this scope (Person/Project/Assignment).
    scope?: TagScope;
    // Restrict the selectable set to the subtree under this tag (its descendants).
    rootTagId?: string;
    // Render the available list as an indented dimension tree (headers + children).
    grouped?: boolean;
    // Parent + scope to stamp on inline-created tags.
    newTagParentId?: string | null;
    newTagAppliesTo?: TagScope;
}

export const TagSelector: React.FC<TagSelectorProps> = ({
    selectedTags,
    onChange,
    placeholder = 'Select tags...',
    isLoading = false,
    className = '',
    allowCreate = false,
    scope,
    rootTagId,
    grouped = false,
    newTagParentId,
    newTagAppliesTo
}) => {
    const {state} = useResourceAllocation();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get all active, in-scope tags from context.
    const allTags = useMemo(() => {
        let list = Object.values(state.tags).filter(
            tag => tag.isActive && tag.id !== null
        );
        if (rootTagId) {
            const descendants = getDescendantIds(
                rootTagId,
                Object.values(state.tags)
            );
            list = list.filter(
                tag => tag.id === rootTagId || descendants.has(tag.id!)
            );
        } else if (scope !== undefined) {
            list = list.filter(tag => tagAppliesTo(tag, scope));
        }
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }, [state.tags, rootTagId, scope]);

    // Filter tags based on search term
    const filteredTags = useMemo(() => {
        if (!searchTerm) return allTags;
        const searchLower = searchTerm.toLowerCase();
        return allTags.filter(tag =>
            tag.name.toLowerCase().includes(searchLower)
        );
    }, [allTags, searchTerm]);

    // When grouped (and not actively searching), render the list as an indented
    // dimension tree; otherwise fall back to the flat list.
    const treeRows = useMemo(() => {
        if (!grouped || searchTerm) return null;
        return flattenTreeForDisplay(filteredTags);
    }, [grouped, searchTerm, filteredTags]);

    // Top-level dimension roots that have children act as non-selectable headers.
    const headerIds = useMemo(() => {
        if (!treeRows) return new Set<string>();
        const parentIds = new Set(
            filteredTags
                .map(t => t.parentTagId)
                .filter((id): id is string => Boolean(id))
        );
        return new Set(
            treeRows
                .filter(r => r.depth === 0 && parentIds.has(r.tag.id!))
                .map(r => r.tag.id!)
        );
    }, [treeRows, filteredTags]);

    // Get selected tag IDs for quick lookup
    const selectedTagIds = useMemo(() => {
        return new Set(
            selectedTags
                .map(t => t.id || (t as TagWithTempId).tempId)
                .filter(Boolean)
        );
    }, [selectedTags]);

    // Check if search term matches an existing tag
    const exactMatch = useMemo(() => {
        const normalizedSearch = finalizeTagName(searchTerm);
        return allTags.some(tag => tag.name === normalizedSearch);
    }, [allTags, searchTerm]);

    const toggle = () => {
        setIsOpen(!isOpen);
        if (!isOpen) {
            setSearchTerm('');
            // Focus search input when opening
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    };

    const handleToggleTag = (tag: Tag) => {
        const tagIdentifier = tag.id || (tag as TagWithTempId).tempId;
        if (selectedTagIds.has(tagIdentifier)) {
            // Remove tag
            onChange(
                selectedTags.filter(t => {
                    const id = t.id || (t as TagWithTempId).tempId;
                    return id !== tagIdentifier;
                })
            );
        } else {
            // Add tag
            onChange([...selectedTags, tag]);
        }
    };

    const handleCreateTag = () => {
        if (!allowCreate || !searchTerm.trim() || exactMatch) return;

        const normalizedName = finalizeTagName(searchTerm);
        if (!normalizedName) return;

        // Create a new tag with temporary ID, stamping the parent + scope so it
        // lands in the right place in the hierarchy.
        const newTag: TagWithTempId = {
            id: null,
            tempId: uuidv4(),
            name: normalizedName,
            colour: getRandomTagColor(),
            icon: getRandomTagIcon(),
            isActive: true,
            createdAt: new Date().toISOString(),
            parentTagId: newTagParentId ?? null,
            appliesTo: newTagAppliesTo ?? scope ?? TagScope.Any
        };

        onChange([...selectedTags, newTag]);
        setSearchTerm('');
    };

    const handleRemoveTag = (tag: Tag | TagWithTempId) => {
        const tagIdentifier = tag.id || (tag as TagWithTempId).tempId;
        onChange(
            selectedTags.filter(t => {
                const id = t.id || (t as TagWithTempId).tempId;
                return id !== tagIdentifier;
            })
        );
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (
            e.key === 'Enter' &&
            allowCreate &&
            searchTerm.trim() &&
            !exactMatch
        ) {
            e.preventDefault();
            handleCreateTag();
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    return (
        <div className={`tag-selector ${className}`}>
            {/* Display selected tags */}
            {selectedTags.length > 0 && (
                <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                    {selectedTags.map(tag => {
                        const key =
                            tag.id || (tag as TagWithTempId).tempId || tag.name;
                        return (
                            <DisplayTag
                                key={key}
                                tag={tag}
                                size="sm"
                                onRemove={() => handleRemoveTag(tag)}
                            />
                        );
                    })}
                </div>
            )}

            {/* Dropdown for selecting/creating tags */}
            <Dropdown isOpen={isOpen} toggle={toggle}>
                <DropdownToggle
                    caret
                    color="light"
                    className="w-100 text-start d-flex align-items-center justify-content-between"
                    disabled={isLoading}
                >
                    <span className="text-muted">
                        {isLoading ? 'Loading...' : placeholder}
                    </span>
                </DropdownToggle>
                <DropdownMenu className="w-100">
                    {/* Search input */}
                    <div className="px-2 pb-2">
                        <div className="position-relative">
                            <Search
                                size={16}
                                className="position-absolute"
                                style={{
                                    left: '8px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#6c757d'
                                }}
                            />
                            <Input
                                innerRef={searchInputRef}
                                type="text"
                                placeholder="Search tags..."
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                                onKeyDown={handleKeyDown}
                                className="ps-4"
                                onClick={e => e.stopPropagation()}
                            />
                        </div>
                    </div>

                    {/* Create new tag option */}
                    {allowCreate && searchTerm.trim() && !exactMatch && (
                        <>
                            <DropdownItem
                                onClick={handleCreateTag}
                                className="d-flex align-items-center gap-2"
                            >
                                <Plus size={16} />
                                <span>
                                    Create "{finalizeTagName(searchTerm)}"
                                </span>
                            </DropdownItem>
                            <DropdownItem divider />
                        </>
                    )}

                    {/* Available tags list */}
                    {filteredTags.length === 0 ? (
                        <DropdownItem disabled>
                            {searchTerm ? 'No tags found' : 'No tags available'}
                        </DropdownItem>
                    ) : treeRows ? (
                        treeRows.map(({tag, depth}) => {
                            const isSelected = selectedTagIds.has(tag.id!);
                            const isHeader = headerIds.has(tag.id!);
                            if (isHeader) {
                                return (
                                    <DropdownItem
                                        key={tag.id}
                                        header
                                        className="text-uppercase small fw-bold text-muted"
                                        style={{paddingLeft: 12 + depth * 16}}
                                    >
                                        {tag.name}
                                    </DropdownItem>
                                );
                            }
                            return (
                                <DropdownItem
                                    key={tag.id}
                                    onClick={() => handleToggleTag(tag)}
                                    className="d-flex align-items-center gap-2"
                                    style={{paddingLeft: 12 + depth * 16}}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                        className="form-check-input"
                                        style={{marginTop: 0}}
                                    />
                                    <DisplayTag tag={tag} size="sm" />
                                </DropdownItem>
                            );
                        })
                    ) : (
                        filteredTags.map(tag => {
                            const isSelected = selectedTagIds.has(tag.id!);
                            return (
                                <DropdownItem
                                    key={tag.id}
                                    onClick={() => handleToggleTag(tag)}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => {}}
                                        className="form-check-input"
                                        style={{marginTop: 0}}
                                    />
                                    <DisplayTag tag={tag} size="sm" />
                                </DropdownItem>
                            );
                        })
                    )}
                </DropdownMenu>
            </Dropdown>
        </div>
    );
};

export default TagSelector;
